import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { toast } from "sonner";

import { haptics } from "@/lib/haptics";

/**
 * The one mutation hook for trip-scoped writes. Wraps the boilerplate every
 * add / edit / delete flow used to hand-roll:
 *
 *   • invalidate the touched query keys when the write settles
 *   • success / error toasts (error falls back to the thrown message)
 *   • haptics: tap on start, success on confirm, error on failure
 *   • optional optimistic update with snapshot + rollback
 *
 * ```ts
 * const remove = useTripMutation({
 *   keys: [queryKeys.trip(trip.id)],
 *   mutationFn: (id: string) => unwrap(removeTripIdea(trip.id, id)),
 *   errorToast: "Couldn't remove.",
 * });
 *
 * const addExpense = useTripMutation<ExpenseEntry, void, Trip>({
 *   keys: [queryKeys.trip(tripId)],
 *   mutationFn: (entry) => unwrap(addExpenseToTrip(tripId, entry)),
 *   applyOptimistic: (_keys, entry) => [
 *     (prev) => prev && { ...prev, expenses: [entry, ...prev.expenses] },
 *   ],
 * });
 * ```
 *
 * `applyOptimistic` receives the keys and the mutation input and returns one
 * `setQueryData` updater per key (or `undefined` to skip that key).
 */

interface UseTripMutationArgs<TArgs, TResult, TSnapshot = unknown> {
  /** Query keys the write touches — invalidated on settle, snapshotted for rollback. */
  keys: readonly QueryKey[];
  mutationFn: (args: TArgs) => Promise<TResult>;
  applyOptimistic?: (
    keys: readonly QueryKey[],
    args: TArgs,
  ) => Array<((prev: TSnapshot | undefined) => TSnapshot | undefined) | undefined>;
  successToast?: string | ((result: TResult, args: TArgs) => string);
  /** Fallback copy when the error has no message. Ignored when `onError` is set. */
  errorToast?: string | ((err: unknown, args: TArgs) => string);
  onSuccess?: (result: TResult, args: TArgs) => void;
  /** Replaces the error toast — for forms that show the error inline. */
  onError?: (err: unknown, args: TArgs) => void;
  /** Invalidate `keys` on settle (default true). */
  invalidateOnSettle?: boolean;
}

export function useTripMutation<TArgs = void, TResult = void, TSnapshot = unknown>(
  opts: UseTripMutationArgs<TArgs, TResult, TSnapshot>,
) {
  const qc = useQueryClient();
  const {
    keys,
    mutationFn,
    applyOptimistic,
    successToast,
    errorToast,
    onSuccess,
    onError,
    invalidateOnSettle = true,
  } = opts;

  return useMutation<TResult, unknown, TArgs, { snapshots: Array<TSnapshot | undefined> }>({
    mutationFn,
    onMutate: async (args) => {
      let snapshots: Array<TSnapshot | undefined> = [];
      if (applyOptimistic) {
        // Freeze in-flight queries so an incoming refetch doesn't clobber
        // the optimistic write between apply and settle.
        await Promise.all(keys.map((k) => qc.cancelQueries({ queryKey: k })));
        snapshots = keys.map((k) => qc.getQueryData<TSnapshot>(k));
        applyOptimistic(keys, args).forEach((u, i) => {
          if (u) qc.setQueryData<TSnapshot>(keys[i]!, u);
        });
      }
      haptics.tap();
      return { snapshots };
    },
    onError: (err, args, ctx) => {
      // Roll back every key we optimistically touched.
      ctx?.snapshots.forEach((snap, i) => qc.setQueryData(keys[i]!, snap));
      haptics.error();
      if (onError) {
        onError(err, args);
        return;
      }
      const fallback = typeof errorToast === "function" ? errorToast(err, args) : errorToast;
      toast.error(
        err instanceof Error && err.message ? err.message : (fallback ?? "Couldn't save changes."),
      );
    },
    onSuccess: (result, args) => {
      haptics.success();
      if (successToast != null) {
        toast.success(
          typeof successToast === "function" ? successToast(result, args) : successToast,
        );
      }
      onSuccess?.(result, args);
    },
    onSettled: () => {
      if (invalidateOnSettle) {
        keys.forEach((k) => qc.invalidateQueries({ queryKey: k }));
      }
    },
  });
}
